/**
 * Client-side GA4 + Meta Pixel analytics wrapper.
 * All browser-side event tracking flows through this module.
 */

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: Record<string, unknown>[];
    fbq: (...args: unknown[]) => void;
  }
}

type AuthMethod = 'email' | 'google' | 'apple';
type MessageType = 'text' | 'voice';
type TTSProvider = 'azure' | 'elevenlabs';
type EchoSource = 'home' | 'search' | 'sidebar' | 'featured';
type CreationStep =
  | 'basic_info'
  | 'social_links'
  | 'personality_test'
  | 'personality_config'
  | 'training';

/** Safely fire a GA4 event. No-ops when gtag is absent. */
function track(event: string, params?: Record<string, unknown>) {
  window.gtag?.('event', event, params);
}

/** Build a standard GA4 e-commerce line item. */
function creditItem(id: string, credits: number, price: number) {
  return { item_id: id, item_name: `${credits} Credits`, price, quantity: 1 };
}

export const analytics = {
  /** Record successful sign-up. */
  signUp: (method: AuthMethod) => track('sign_up', { method }),

  /** Record successful login. */
  login: (method: AuthMethod) => track('login', { method }),

  /** Record failed sign-up attempt. */
  signUpFailed: (method: AuthMethod, errorType: string) =>
    track('sign_up_failed', { method, error_type: errorType }),

  /** Record failed login attempt. */
  loginFailed: (method: AuthMethod, errorType: string) =>
    track('login_failed', { method, error_type: errorType }),

  /** Record OAuth welcome-bonus credit award. */
  oauthBonusAwarded: (method: 'google' | 'apple', credits: number) =>
    track('oauth_bonus_awarded', { method, credits }),

  /** Record a chat message sent by the user. */
  messageSent: (echoId: string, echoName: string, type: MessageType, creditsCost = 0) =>
    track('message_sent', {
      echo_id: echoId,
      echo_name: echoName,
      message_type: type,
      credits_cost: creditsCost,
    }),

  /** Record a conversation being opened. */
  conversationOpened: (echoId: string, echoName: string, isFirstConversation = false) =>
    track('conversation_opened', {
      echo_id: echoId,
      echo_name: echoName,
      is_first_conversation: isFirstConversation,
    }),

  /** Record a failed message send (e.g. insufficient credits). */
  messageSendFailed: (echoId: string, errorType: string, creditsRequired?: number) =>
    track('message_send_failed', {
      echo_id: echoId,
      error_type: errorType,
      credits_required: creditsRequired,
    }),

  /** Record TTS audio playback start. */
  audioPlaybackStarted: (echoId: string, messageId: string, ttsProvider: TTSProvider) =>
    track('audio_playback_started', {
      echo_id: echoId,
      message_id: messageId,
      tts_provider: ttsProvider,
    }),

  /** Record a voice message sent by the user. */
  voiceMessageSent: (echoId: string, durationSeconds: number) =>
    track('voice_message_sent', { echo_id: echoId, duration_seconds: durationSeconds }),

  /** Record conversation history cleared. */
  conversationCleared: (echoId: string) => track('conversation_cleared', { echo_id: echoId }),

  /** Record a message liked by the user. */
  messageLiked: (echoId: string, messageId: string) =>
    track('message_liked', { echo_id: echoId, message_id: messageId }),

  /** Record a message unliked by the user. */
  messageUnliked: (echoId: string, messageId: string) =>
    track('message_unliked', { echo_id: echoId, message_id: messageId }),

  /** Record echo selection from discovery surfaces. */
  echoSelected: (echoId: string, echoName: string, source: EchoSource) =>
    track('select_content', {
      content_type: 'echo',
      item_id: echoId,
      item_name: echoName,
      source,
    }),

  /** Record a search query in echo discovery. */
  echoSearched: (query: string, resultsCount: number) =>
    track('search', { search_term: query, results_count: resultsCount }),

  /** Record a filter applied in echo discovery. */
  echoFilterApplied: (filterType: string, filterValue: string, resultsCount: number) =>
    track('filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
      results_count: resultsCount,
    }),

  /** Record home page view. */
  homePageViewed: () => track('page_view', { page_title: 'Home', page_location: '/home' }),

  /** Record click on "Create Echo" CTA. */
  createEchoCTAClicked: (source: 'home' | 'sidebar' | 'account') =>
    track('create_echo_cta_clicked', { source }),

  /** Record echo creation wizard started. */
  echoCreationStarted: (draftId: string) => track('echo_creation_started', { draft_id: draftId }),

  /** Record a step completed in the creation wizard. */
  echoCreationStep: (draftId: string, step: number, stepName: CreationStep) =>
    track('echo_creation_step', { draft_id: draftId, step, step_name: stepName }),

  /** Record a successfully published echo. */
  echoPublished: (echoId: string, echoName: string) =>
    track('echo_published', { echo_id: echoId, echo_name: echoName }),

  /** Record a failed echo publish attempt. */
  echoPublishFailed: (draftId: string, errorType: string) =>
    track('echo_publish_failed', { draft_id: draftId, error_type: errorType }),

  /** Record credit packages list viewed. */
  creditPackagesViewed: (currentBalance: number) =>
    track('view_item_list', {
      item_list_id: 'credit_packages',
      item_list_name: 'Credit Packages',
      current_balance: currentBalance,
    }),

  /** Record checkout started for a credit package. */
  purchaseStarted: (packageId: string, credits: number, priceUSD: number) =>
    track('begin_checkout', {
      currency: 'USD',
      value: priceUSD,
      items: [creditItem(packageId, credits, priceUSD)],
    }),

  /** Record completed purchase. */
  purchaseCompleted: (transactionId: string, credits: number, priceUSD: number) =>
    track('purchase', {
      transaction_id: transactionId,
      currency: 'USD',
      value: priceUSD,
      items: [creditItem('credits', credits, priceUSD)],
    }),

  /** Record profile fields updated. */
  profileUpdated: (fieldsUpdated: string[]) =>
    track('profile_updated', { fields_updated: fieldsUpdated.join(',') }),

  /** Record account deletion request. */
  accountDeletionRequested: () => track('account_deletion_requested'),

  /** Fire Meta Pixel CompleteRegistration event. */
  completeRegistration: () => {
    window.fbq?.('track', 'CompleteRegistration');
  },

  /** Push account_created to dataLayer for GTM triggers. */
  accountCreated: (userId: string) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'account_created',
      registration_status: 'success',
      user_id: userId,
    });
  },
};
